/**
 * HistoryChartTab — History inner tab of the patient Chart shell.
 *
 * Flutter ref: `patient_chart/.../tab_widgets/history_tab.dart`.
 *
 * Two sections:
 *   - **Family medical history** — relatives' diagnoses + demographics.
 *     A single row can carry multiple `code[]` entries (one relative,
 *     multiple inherited conditions).
 *   - **Social history** — lifestyle observations (smoking, alcohol,
 *     occupation, housing, exercise). One row per observation type.
 *
 * Each section fetches independently. Tombstones (`isDeleted: true`)
 * filtered client-side, strict `!== true` (Samer PR #157 pattern).
 *
 * **Write surfaces (PROD-827):** Add / Edit / Delete shipped 2026-05-20.
 * Only one editor (Family OR Social) can be open at a time — Flutter
 * enforces the same with its `isEdit` flag and the "Only one item can
 * be edited at a time" error toast (L191/322).
 */

import { useReportChartCount } from './chart-page-context';
import { ChartShowAll } from './chart-row-cap';
import { useRowCap } from './use-row-cap';
import { useState } from 'react';
import { toast } from 'sonner';
import { ModalYesNo, Skeleton } from '@scripsteam/scrips-signal-ds';
import {
  usePatientFamilyHistory,
  usePatientSocialHistory,
} from '@/patients/services/use-patient-history';
import { useUpdateSocialHistory } from '@/patients/services/use-update-social-history';
import { useUpdateFamilyMedicalHistory } from '@/patients/services/use-update-family-medical-history';
import { normalizePerformerDetails } from './add-allergy-dialog';
import { ChartSectionHeader } from './chart-section-header';
import { ChartRowIconActions } from '@/shared/ui/components/chart-row-actions';
import { AddSocialHistoryEditor } from './add-social-history-editor';
import { AddFamilyHistoryEditor } from './add-family-history-editor';
import type {
  FamilyHistoryCodeElement,
  PatientFamilyHistory,
  PatientSocialHistory,
  UpdateFamilyMedicalHistoryBody,
  UpdateSocialHistoryBody,
} from '@/types/patient-chart-api';
import { V2_BODY_EMPHASIS, v2Look } from '@/shared/ui/design-v2';
import { CHART_TD_V2, CHART_TH_V2, chartCell, chartTableCard, type ChartCell } from './chart-table-v2';
import { EmptyState } from '@scripsteam/scrips-signal-ds';

export interface HistoryChartTabProps {
  patientId: string | null | undefined;
}

type EditorState =
  | { kind: 'none' }
  | { kind: 'add-social' }
  | { kind: 'edit-social'; row: PatientSocialHistory }
  | { kind: 'add-family' }
  | { kind: 'edit-family'; row: PatientFamilyHistory };

type Deleting =
  | { kind: 'social'; row: PatientSocialHistory }
  | { kind: 'family'; row: PatientFamilyHistory }
  | null;

export function HistoryChartTab({ patientId }: HistoryChartTabProps) {
  const familyQ = usePatientFamilyHistory(patientId);
  const socialQ = usePatientSocialHistory(patientId);
  const updateSocialMut = useUpdateSocialHistory();
  const updateFamilyMut = useUpdateFamilyMedicalHistory();

  const family = (familyQ.data ?? []).filter((r) => r.isDeleted !== true);
  const social = (socialQ.data ?? []).filter((r) => r.isDeleted !== true);
  // The index's count: family and social history together (nothing while either loads).
  useReportChartCount(familyQ.isLoading || socialQ.isLoading ? null : family.length + social.length);

  const [editor, setEditor] = useState<EditorState>({ kind: 'none' });
  const [deleting, setDeleting] = useState<Deleting>(null);

  const editorOpen = editor.kind !== 'none';

  const closeEditor = () => setEditor({ kind: 'none' });

  const startEditSocial = (row: PatientSocialHistory) => {
    if (editorOpen) {
      toast.error('Only one item can be edited at a time');
      return;
    }
    setEditor({ kind: 'edit-social', row });
  };

  const startEditFamily = (row: PatientFamilyHistory) => {
    if (editorOpen) {
      toast.error('Only one item can be edited at a time');
      return;
    }
    setEditor({ kind: 'edit-family', row });
  };

  // Soft-delete via PUT /socialHistory/{id} with isDeleted: true.
  // Same strict createdBy guard as the editor — refuse to ship rather
  // than substitute current user.
  const confirmDeleteSocial = async (row: PatientSocialHistory) => {
    if (!row.identifier || !patientId) return;
    if (!row.createdBy) {
      toast.error(
        'Cannot delete — this record is missing its original recorder. Please contact support.',
      );
      return;
    }
    const body: UpdateSocialHistoryBody = {
      identifier: row.identifier,
      code: row.code
        ? {
            code: row.code.code ?? null,
            display: row.code.display ?? null,
            system: row.code.system ?? null,
            category: null,
          }
        : null,
      status: row.status === 'Inactive' ? 'Inactive' : 'Active',
      category: row.category ?? null,
      comment: row.comment ?? '',
      subject: row.subject ?? patientId,
      encounter: row.encounter ?? null,
      effectiveDateTime: null,
      fromDisplayTime: row.fromDisplayTime ?? null,
      fromTime: row.fromTime ?? null,
      toDisplayTime: row.toDisplayTime ?? null,
      toTime: row.toTime ?? null,
      historyCode: row.historyCode
        ? {
            code: row.historyCode.code ?? null,
            // PascalCase Definition preserved verbatim from the
            // original record. Read-side type only carries Coding
            // basics; ship null if not present.
            Definition: null,
            display: row.historyCode.display ?? null,
            system: row.historyCode.system ?? null,
            description: null,
            shortName: null,
          }
        : null,
      dataAbsentReason: 'n/a',
      isDeleted: true,
      createdBy: row.createdBy,
      createdDate: row.createdDate ?? null,
      updatedby: null,
      updatedDate: null,
      performerDetails: normalizePerformerDetails(row.performerDetails),
      speciality: row.speciality ?? null,
    };
    try {
      await updateSocialMut.mutateAsync({
        patientId,
        socialHistoryId: row.identifier,
        body,
      });
      toast.success(
        `Removed social history: ${row.code?.display ?? 'Unknown'}`,
      );
      setDeleting(null);
    } catch {
      /* Global toast surfaces failure; keep dialog open. */
    }
  };

  // Soft-delete via PUT /familyMedicalHistory/{id} with isDeleted: true.
  // The code[] array is round-tripped verbatim (with each row's
  // per-row performer/speciality preserved).
  const confirmDeleteFamily = async (row: PatientFamilyHistory) => {
    if (!row.identifier || !patientId) return;
    if (!row.createdBy) {
      toast.error(
        'Cannot delete — this record is missing its original recorder. Please contact support.',
      );
      return;
    }
    const wireCodes: FamilyHistoryCodeElement[] = (row.code ?? []).map((c) => ({
      system: c.system ?? null,
      code: c.code ?? null,
      display: c.display ?? null,
      description: c.description ?? null,
      fromTime: c.fromTime ?? null,
      fromDisplayTime: c.fromDisplayTime ?? null,
      toTime: c.toTime ?? null,
      toDisplayTime: c.toDisplayTime ?? null,
      isDeleted: c.isDeleted === true,
      updatedby: c.updatedby ?? null,
      updatedDate: c.updatedDate ?? null,
      performerDetails: normalizePerformerDetails(c.performerDetails),
      speciality: c.speciality ?? null,
    }));
    const body: UpdateFamilyMedicalHistoryBody = {
      identifier: row.identifier,
      code: wireCodes,
      status: 'Active',
      patient: row.patient ?? patientId,
      dataAbsentReason: 'n/a',
      fullName: 'n/a',
      relationship: row.relationship ?? null,
      gender: null,
      age: 0,
      dob: null,
      outCome: 'n/a',
      note: row.note ?? '',
      statusReason: null,
      encounter: row.encounter ?? null,
      isDeleted: true,
      createdDate: row.createdDate ?? null,
      createdBy: row.createdBy,
    };
    try {
      await updateFamilyMut.mutateAsync({
        patientId,
        familyHistoryId: row.identifier,
        body,
      });
      toast.success(
        `Removed family history: ${row.relationship?.display ?? 'Unknown'}`,
      );
      setDeleting(null);
    } catch {
      /* Global toast surfaces failure; keep dialog open. */
    }
  };

  if (!patientId) {
    return (
      <div className="p-6">
        <EmptyState
            icon="info"
            title="Save the patient first to record history."
          />
      </div>
    );
  }

  const isLoading = familyQ.isLoading || socialQ.isLoading;
  const isError = familyQ.isError || socialQ.isError;
  const isDeletePending =
    updateSocialMut.isPending || updateFamilyMut.isPending;

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <EmptyState
            icon="warning"
            title="Couldn't load history."
            ctaLabel="Retry"
            onCtaClick={() => { void familyQ.refetch(); void socialQ.refetch(); }}
          />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <FamilySection
        rows={family}
        editor={editor}
        onAddOpen={() => setEditor({ kind: 'add-family' })}
        onEdit={startEditFamily}
        onDelete={(row) => setDeleting({ kind: 'family', row })}
        onClose={closeEditor}
        patientId={patientId}
      />
      <SocialSection
        rows={social}
        editor={editor}
        onAddOpen={() => setEditor({ kind: 'add-social' })}
        onEdit={startEditSocial}
        onDelete={(row) => setDeleting({ kind: 'social', row })}
        onClose={closeEditor}
        patientId={patientId}
      />

      <ModalYesNo
        open={deleting !== null}
        onCancel={() => setDeleting(null)}
        title="Are you sure?"
        message={
          <>
            Do you want to proceed on deleting{' '}
            <span className={v2Look('font-semibold text-brand-depth', V2_BODY_EMPHASIS)}>
              {deleting?.kind === 'social'
                ? (deleting.row.code?.display ?? 'this social history')
                : deleting?.kind === 'family'
                  ? (deleting.row.relationship?.display ??
                    'this family history')
                  : ''}
            </span>
            ?
          </>
        }
        confirmLabel={isDeletePending ? 'Deleting…' : 'Delete'}
        destructive
        loading={isDeletePending}
        onConfirm={() => {
          // `ModalYesNo` doesn't auto-close (no Radix action to preventDefault
          // on) — each branch below already calls `setDeleting(null)` itself
          // on success (see `confirmDeleteSocial` / `confirmDeleteFamily`).
          if (deleting?.kind === 'social') {
            void confirmDeleteSocial(deleting.row);
          } else if (deleting?.kind === 'family') {
            void confirmDeleteFamily(deleting.row);
          }
        }}
      />
    </div>
  );
}

// ── Family history section ───────────────────────────────────────────────────

function FamilySection({
  rows,
  editor,
  onAddOpen,
  onEdit,
  onDelete,
  onClose,
  patientId,
}: {
  rows: PatientFamilyHistory[];
  editor: EditorState;
  onAddOpen: () => void;
  onEdit: (row: PatientFamilyHistory) => void;
  onDelete: (row: PatientFamilyHistory) => void;
  onClose: () => void;
  patientId: string;
}) {
  const isEditingThis =
    editor.kind === 'add-family' || editor.kind === 'edit-family';
  const editingRow = editor.kind === 'edit-family' ? editor.row : null;
  const editorOpen = editor.kind !== 'none';
  const cap = useRowCap(rows);

  return (
    <div>
      <ChartSectionHeader
        title="Family medical history"
        count={rows.length}
        onAdd={editorOpen ? undefined : onAddOpen}
      />
      {isEditingThis ? (
        <AddFamilyHistoryEditor
          key={editingRow?.identifier ?? 'add'}
          patientId={patientId}
          editing={editingRow}
          onClose={onClose}
        />
      ) : null}
      {rows.length === 0 ? (
        isEditingThis ? null : (
          <EmptyState icon="clipboard" title="No family history recorded for this patient." ctaLabel="Add family history" onCtaClick={editorOpen ? undefined : onAddOpen} />
        )
      ) : (
        <div className={chartTableCard('mt-2 rounded-lg border border-border-default overflow-hidden bg-surface-card')}>
          <table className="w-full text-sm">
            <thead className="bg-grey-100">
              <tr className="text-left text-text-muted">
                <Th>#</Th>
                <Th>Relative</Th>
                <Th>Relationship</Th>
                <Th>Gender</Th>
                <Th>Age</Th>
                <Th>Conditions</Th>
                <Th>Outcome</Th>
                <Th>Note</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {cap.visible.map((r, i) => (
                <tr
                  key={r.identifier ?? `${i}-${r.fullName ?? 'unknown'}`}
                  className="border-t border-border-default hover:bg-grey-100/50"
                >
                  <Td cell="index">{i + 1}</Td>
                  <Td cell="name">
                    <span className={v2Look('font-medium text-brand-depth', V2_BODY_EMPHASIS)}>
                      {r.fullName?.trim() && r.fullName.trim().toLowerCase() !==
                      'n/a'
                        ? r.fullName.trim()
                        : '—'}
                    </span>
                  </Td>
                  <Td cell="text">{r.relationship?.display ?? '—'}</Td>
                  <Td cell="short">{r.gender?.trim() || '—'}</Td>
                  <Td cell="short">{r.age != null && r.age !== 0 ? `${r.age}` : '—'}</Td>
                  <Td cell="text">
                    {/* `code` is an array — multiple conditions per
                        relative on a single row. Join displays with "·" */}
                    {(r.code ?? []).length === 0
                      ? '—'
                      : (r.code ?? [])
                          .map((c) => c.display ?? c.code ?? '—')
                          .join(' · ')}
                  </Td>
                  <Td cell="text">
                    {r.outCome?.trim() &&
                    r.outCome.trim().toLowerCase() !== 'n/a'
                      ? r.outCome.trim()
                      : '—'}
                  </Td>
                  <Td cell="note" tip={r.note?.trim() || '—'}>{r.note?.trim() || '—'}</Td>
                  <Td align="right" cell="actions">
                    <ChartRowIconActions
                      editLabel={`Edit family history: ${r.relationship?.display ?? 'Unknown'}`}
                      deleteLabel={`Delete family history: ${r.relationship?.display ?? 'Unknown'}`}
                      onEdit={editorOpen ? undefined : () => onEdit(r)}
                      onDelete={editorOpen ? undefined : () => onDelete(r)}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ChartShowAll cap={cap} label="family medical history" />
    </div>
  );
}

// ── Social history section ───────────────────────────────────────────────────

function SocialSection({
  rows,
  editor,
  onAddOpen,
  onEdit,
  onDelete,
  onClose,
  patientId,
}: {
  rows: PatientSocialHistory[];
  editor: EditorState;
  onAddOpen: () => void;
  onEdit: (row: PatientSocialHistory) => void;
  onDelete: (row: PatientSocialHistory) => void;
  onClose: () => void;
  patientId: string;
}) {
  const isEditingThis =
    editor.kind === 'add-social' || editor.kind === 'edit-social';
  const editingRow = editor.kind === 'edit-social' ? editor.row : null;
  const editorOpen = editor.kind !== 'none';
  const cap = useRowCap(rows);

  return (
    <div>
      <ChartSectionHeader
        title="Social history"
        count={rows.length}
        onAdd={editorOpen ? undefined : onAddOpen}
      />
      {isEditingThis ? (
        <AddSocialHistoryEditor
          key={editingRow?.identifier ?? 'add'}
          patientId={patientId}
          editing={editingRow}
          onClose={onClose}
        />
      ) : null}
      {rows.length === 0 ? (
        isEditingThis ? null : (
          <EmptyState icon="clipboard" title="No social history recorded for this patient." ctaLabel="Add social history" onCtaClick={editorOpen ? undefined : onAddOpen} />
        )
      ) : (
        <div className={chartTableCard('mt-2 rounded-lg border border-border-default overflow-hidden bg-surface-card')}>
          <table className="w-full text-sm">
            <thead className="bg-grey-100">
              <tr className="text-left text-text-muted">
                <Th>#</Th>
                <Th>Observation</Th>
                <Th>Category</Th>
                <Th>Value</Th>
                <Th>From</Th>
                <Th>To</Th>
                <Th>Recorder</Th>
                <Th>Comment</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {cap.visible.map((r, i) => (
                <tr
                  key={r.identifier ?? `${i}-${r.code?.code ?? 'unknown'}`}
                  className="border-t border-border-default hover:bg-grey-100/50"
                >
                  <Td cell="index">{i + 1}</Td>
                  <Td cell="name">
                    <span className={v2Look('font-medium text-brand-depth', V2_BODY_EMPHASIS)}>
                      {r.code?.display ?? '—'}
                    </span>
                  </Td>
                  <Td cell="text">{r.category?.display ?? '—'}</Td>
                  <Td cell="text">{r.historyCode?.display ?? '—'}</Td>
                  <Td date>{formatDate(r.fromTime, r.fromDisplayTime)}</Td>
                  <Td date>{formatDate(r.toTime, r.toDisplayTime)}</Td>
                  <Td cell="text">{r.performerDetails?.name ?? r.createdBy ?? '—'}</Td>
                  <Td cell="note" tip={r.comment?.trim() || '—'}>{r.comment?.trim() || '—'}</Td>
                  <Td align="right" cell="actions">
                    <ChartRowIconActions
                      editLabel={`Edit social history: ${r.code?.display ?? 'Unknown'}`}
                      deleteLabel={`Delete social history: ${r.code?.display ?? 'Unknown'}`}
                      onEdit={editorOpen ? undefined : () => onEdit(r)}
                      onDelete={editorOpen ? undefined : () => onDelete(r)}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ChartShowAll cap={cap} label="social history" />
    </div>
  );
}

// ── Shared cells ─────────────────────────────────────────────────────────────

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={chartCell(
        `px-3 py-2 text-xs font-bold uppercase tracking-wide ${
          align === 'right' ? 'text-right' : 'text-left'
        }`,
        CHART_TH_V2,
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
  date = false,
  cell,
  tip,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
  /** What the column holds (the bridge sizes it: index, name, text, short, date, note, actions). */
  cell?: ChartCell;
  /** A note's full text for the app's instant tooltip, shown when the one-line cell is cut off. */
  tip?: string;
  /** A date: kept on one line (the bridge's "patient chart" block, `data-chart-cell="date"`). */
  date?: boolean;
}) {
  return (
    <td
      data-chart-cell={date ? 'date' : cell}
      data-tip={tip}
      className={chartCell(
        `px-3 py-2 align-top ${
          align === 'right' ? 'text-right' : 'text-left'
        }`,
        CHART_TD_V2,
      )}
    >
      {children}
    </td>
  );
}

function formatDate(
  iso: string | null | undefined,
  displayFallback?: string | null,
): string {
  if (iso) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      });
    }
  }
  if (displayFallback?.trim()) return displayFallback;
  return '—';
}
